import React, { Component } from 'react';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSortMultiple, dynamicSort } from '@/shared/utils/dynamicSort';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import CompanySelector from '@/shared/components/input/CompanySelector';
import DayMonthPicker from '@/shared/components/input/DayMonthPicker';
import UserSearch from '@/shared/components/input/UserSearch';
import EnumSelect from '@/shared/components/input/EnumSelect';
import LanguageSelect from '@/shared/components/input/LanguageSelect';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import api from '@/shared/api';
import config from '@/config';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import messages from '../relationOverviewMessages';
import tagSettingsMessages from '@/modules/settings/Tags/tagSettingsMessages';

const sendInvoiceSoftware = 'a85c1650-ba4e-4b8b-af9b-e1b35aef34e1';
const otherSoftware = 'd4d5bf76-55ce-4030-91bb-6416f5636505';
const relationGroupField = '42266463-8da3-46a2-822a-cd99ffd96bc8';
const invoiceDetailTypeField = 'a13b1196-20ed-43f1-b920-7831bec7201b';
const invoiceTemplateField = 'd19124ba-3320-4b1d-bccf-63c071502f31';
const invoiceDetailTemplateField = '8aaed4a8-909e-47e0-bc02-f6a710bab097';
const creditNoteTemplateField = '754804e9-a34e-4c85-9f67-67ee503f0269';

const DONT_UPDATE = 'dont-update';
const USE_GENERAL_SETTING = 'use-general-setting';

const TEMPLATE_TYPE_MAP = {
  [invoiceTemplateField]: 0,
  [invoiceDetailTemplateField]: 1,
  [creditNoteTemplateField]: 2,
};

const isTemplateField = fieldId =>
  fieldId === invoiceTemplateField ||
  fieldId === invoiceDetailTemplateField ||
  fieldId === creditNoteTemplateField;

export default class UpdateRelationFieldModal extends Component {
  state = {
    action: 'add',
    fields: [],
    legalForms: [],
    selectedItems: [],
    relationGroups: [],
    allTemplates: [],
    templateSelections: {},
    resolvedCompanyId: null,
    loadingTemplates: false,
    newGroupName: '',
    showConfirmCreate: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    companyId: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
  };

  static defaultProps = {
    companyId: null,
    languages: [],
  };

  componentDidMount() {
    this.setAvailableFields();
    this.loadRelationGroups();
    this.resolveCompanyId();
  }

  loadRelationGroups = () => {
    api.base
      .get(`${config.appUrls.api}/api/RelationGroup/GetAll`)
      .then(({ data }) => {
        this.setState({ relationGroups: (data ?? []).sort(dynamicSort('name')) });
      })
      .catch(() => {});
  };

  resolveCompanyId = () => {
    const { companyId } = this.props;
    if (companyId) {
      this.setState({ resolvedCompanyId: companyId });
      return;
    }
    if (!hasMultipleCompanies()) {
      const defaultCompanyId = DataCacheService.getDefaultCompany()?.id;
      this.setState({ resolvedCompanyId: defaultCompanyId || null });
    }
  };

  loadTemplates = () => {
    const { resolvedCompanyId } = this.state;
    if (!resolvedCompanyId) return;

    this.setState({ loadingTemplates: true });

    api.base
      .get(`${config.appUrls.api}/api/documentTemplate/Templates`, {
        params: { templateTypes: [0, 1, 2], companyId: resolvedCompanyId },
      })
      .then(({ data }) => {
        this.setState({
          allTemplates: (data ?? [])
            .map(t => ({
              id: t.id,
              name: t.name,
              templateType: t.templateType,
              languageCode: t.languageCode,
            }))
            .sort(dynamicSortMultiple('name')),
          loadingTemplates: false,
        });
      })
      .catch(() => {
        this.setState({ loadingTemplates: false });
      });
  };

  templateChanged = (langCode, value) => {
    this.setState(({ templateSelections }) => ({
      templateSelections: { ...templateSelections, [langCode]: value },
    }));
  };

  getTemplatesForLanguage = langCode => {
    const { allTemplates, selectedField } = this.state;
    if (!selectedField || !isTemplateField(selectedField.id)) return [];

    const templateType = TEMPLATE_TYPE_MAP[selectedField.id];
    return allTemplates.filter(t => t.templateType === templateType && t.languageCode === langCode);
  };

  actionChanged = event => {
    const action = event.target.value;
    this.setState({
      action,
    });
  };

  getLegalForms = () => {
    const { intl } = this.props;
    const dataCache = DataCacheService.getDataCache();
    if (!dataCache || !dataCache.legalForm) return [];
    return dataCache.legalForm
      .map(x => ({
        id: x.id,
        label: intl.formatMessage(listItemMsg('legalform', x.id, x.description || '')),
      }))
      .sort(dynamicSortMultiple('label'));
  };

  setAvailableFields = () => {
    const { intl } = this.props;

    const availableFields = [
      {
        id: '0443d6e0-343d-437f-907c-84235907a5f8',
        label: intl.formatMessage({
          id: `field_0443d6e0-343d-437f-907c-84235907a5f8`.toLowerCase(),
          defaultMessage: 'Legal form',
        }),
      },
      {
        id: 'e31bb7ad-afb4-4a37-bd1e-46d4a9a71cf0',
        label: intl.formatMessage({
          id: `field_e31bb7ad-afb4-4a37-bd1e-46d4a9a71cf0`.toLowerCase(),
          defaultMessage: 'Own company',
        }),
      },
      {
        id: '3117d514-64bc-4c6e-84f7-4a39fbdc51e2',
        label: intl.formatMessage({
          id: `field_3117d514-64bc-4c6e-84f7-4a39fbdc51e2`.toLowerCase(),
          defaultMessage: 'End fiscal year',
        }),
      },
      {
        id: 'ebf5a67c-6ac2-4616-84ea-2b475f279575',
        label: intl.formatMessage({
          id: `field_ebf5a67c-6ac2-4616-84ea-2b475f279575`.toLowerCase(),
          defaultMessage: 'VAT periodicity',
        }),
      },
      {
        id: 'd4f04a11-cdf7-4d85-900f-c1d15df6057f',
        label: intl.formatMessage({
          id: `field_d4f04a11-cdf7-4d85-900f-c1d15df6057f`.toLowerCase(),
          defaultMessage: 'Financial statement scheme',
        }),
      },
      {
        id: 'b33726aa-3dbb-4e6a-afce-219670843eb5',
        label: intl.formatMessage({
          id: `field_b33726aa-3dbb-4e6a-afce-219670843eb5`.toLowerCase(),
          defaultMessage: 'Social secretariat',
        }),
      },
      {
        id: '99c41dc8-a3fb-4843-bbbf-1c558c46f90f',
        label: intl.formatMessage({
          id: `field_99c41dc8-a3fb-4843-bbbf-1c558c46f90f`.toLowerCase(),
          defaultMessage: 'Social security fund',
        }),
      },
      {
        id: 'b094d68c-1c90-422a-a64a-2913b07e02f1',
        label: intl.formatMessage({
          id: `field_b094d68c-1c90-422a-a64a-2913b07e02f1`.toLowerCase(),
          defaultMessage: 'Payment method',
        }),
      },
      {
        id: '56495285-77d0-46bf-951a-4a66e14e1298',
        label: intl.formatMessage({
          id: `field_56495285-77d0-46bf-951a-4a66e14e1298`.toLowerCase(),
          defaultMessage: 'Accounting software',
        }),
      },
      {
        id: '4326316b-07eb-4ef6-889e-6382b38a23a3',
        label: intl.formatMessage({
          id: `field_4326316b-07eb-4ef6-889e-6382b38a23a3`.toLowerCase(),
          defaultMessage: 'Receive invoice software',
        }),
      },
      {
        id: '8a884985-efa6-4faf-8740-6461caf69aed',
        label: intl.formatMessage({
          id: `field_8a884985-efa6-4faf-8740-6461caf69aed`.toLowerCase(),
          defaultMessage: 'Language',
        }),
      },
      {
        id: '246f74be-a60d-46b3-82d7-294dfa227762',
        label: intl.formatMessage({
          id: `field_246f74be-a60d-46b3-82d7-294dfa227762`.toLowerCase(),
          defaultMessage: 'Share register',
        }),
      },
      {
        id: '40ae0a55-fd7d-4577-830f-22589642e875',
        label: intl.formatMessage({
          id: `field_40ae0a55-fd7d-4577-830f-22589642e875`.toLowerCase(),
          defaultMessage: 'Send invoice strategy',
        }),
      },
      {
        id: 'b5813c30-a8f9-4b66-a0d5-236f400c5088',
        label: intl.formatMessage({
          id: `field_b5813c30-a8f9-4b66-a0d5-236f400c5088`.toLowerCase(),
          defaultMessage: 'InvoiceEmailAttachmentOptions',
        }),
      },
      {
        id: '5a9630e2-12c3-4d80-9745-2330bd45ef1a',
        label: intl.formatMessage({
          id: `field_5a9630e2-12c3-4d80-9745-2330bd45ef1a`.toLowerCase(),
          defaultMessage: 'SendInvoicePeppolReady',
        }),
      },
      {
        id: sendInvoiceSoftware,
        label: intl.formatMessage({
          id: `field_a85c1650-ba4e-4b8b-af9b-e1b35aef34e1`.toLowerCase(),
          defaultMessage: 'SendInvoiceSoftware',
        }),
      },
      {
        id: otherSoftware,
        label: intl.formatMessage({
          id: `field_d4d5bf76-55ce-4030-91bb-6416f5636505`.toLowerCase(),
          defaultMessage: 'OtherSoftware',
        }),
      },
      {
        id: relationGroupField,
        label: intl.formatMessage({
          id: `field_${relationGroupField}`.toLowerCase(),
          defaultMessage: 'Relation group',
        }),
      },
      {
        id: invoiceDetailTypeField,
        label: intl.formatMessage({
          id: `field_${invoiceDetailTypeField}`.toLowerCase(),
          defaultMessage: 'Invoice detail type',
        }),
      },
      {
        id: invoiceTemplateField,
        label: intl.formatMessage({
          id: `field_${invoiceTemplateField}`.toLowerCase(),
          defaultMessage: 'Invoice template',
        }),
      },
      {
        id: invoiceDetailTemplateField,
        label: intl.formatMessage({
          id: `field_${invoiceDetailTemplateField}`.toLowerCase(),
          defaultMessage: 'Invoice detail template',
        }),
      },
      {
        id: creditNoteTemplateField,
        label: intl.formatMessage({
          id: `field_${creditNoteTemplateField}`.toLowerCase(),
          defaultMessage: 'Credit note template',
        }),
      },
    ];

    this.setState({
      fields: availableFields.sort(dynamicSort('label')),
      legalForms: this.getLegalForms(),
    });
  };

  fieldChanged = event => {
    const { fields } = this.state;
    const fieldId = event.target.value;
    const field = fields.find(x => x.id === fieldId);

    if (!field) return;

    this.setState(state => ({
      ...state,
      selectedField: field,
      selectedValue: field.defaultValue ?? undefined,
      selectedItems: [],
      action: 'add',
      newGroupName: '',
      templateSelections: {},
    }));

    if (isTemplateField(fieldId)) {
      this.loadTemplates();
    }
  };

  selectedValueChanged = value => {
    this.setState(state => ({
      ...state,
      selectedValue: value,
    }));
  };

  isFormInvalid = () => {
    const {
      selectedField,
      selectedValue,
      selectedItems,
      newGroupName,
      templateSelections,
      resolvedCompanyId,
    } = this.state;
    if (!selectedField) return true;

    if (selectedField.id === sendInvoiceSoftware || selectedField.id === otherSoftware) {
      return selectedItems.length === 0;
    }

    if (selectedField.id === relationGroupField) {
      return selectedValue === undefined && newGroupName === '';
    }

    if (isTemplateField(selectedField.id)) {
      if (!resolvedCompanyId) return true;
      return !Object.values(templateSelections).some(v => v !== DONT_UPDATE);
    }

    if (
      // list of fields that allow null are listed here
      selectedField.id !== '246f74be-a60d-46b3-82d7-294dfa227762' &&
      selectedField.id !== '40ae0a55-fd7d-4577-830f-22589642e875' &&
      selectedField.id !== 'b5813c30-a8f9-4b66-a0d5-236f400c5088' &&
      selectedField.id !== invoiceDetailTypeField &&
      !selectedValue &&
      selectedValue !== 0
    )
      return true;

    return false;
  };

  handleConfirmCreateGroup = () => {
    const { newGroupName } = this.state;
    const { onSave } = this.props;

    api.base
      .post(`${config.appUrls.api}/api/RelationGroup`, { name: newGroupName })
      .then(({ data }) => {
        this.setState({ showConfirmCreate: false });
        onSave(
          {
            definition: { id: relationGroupField },
            value: data.id,
          },
          null,
        );
      })
      .catch(() => {
        this.setState({ showConfirmCreate: false });
      });
  };

  onSave = () => {
    const {
      selectedField,
      selectedValue,
      selectedItems,
      action,
      newGroupName,
      templateSelections,
    } = this.state;
    const { onSave } = this.props;

    if (selectedField.id === relationGroupField && newGroupName) {
      this.setState({ showConfirmCreate: true });
      return;
    }

    if (isTemplateField(selectedField.id)) {
      const documentTemplateIds = {};
      Object.entries(templateSelections).forEach(([langCode, value]) => {
        if (value === DONT_UPDATE) return;
        documentTemplateIds[langCode] = value === USE_GENERAL_SETTING ? null : value;
      });
      return onSave({ definition: { id: selectedField.id }, documentTemplateIds }, null);
    }

    const fieldUpdate =
      selectedField.id === sendInvoiceSoftware || selectedField.id === otherSoftware
        ? {
            softwares: selectedItems,
            action,
            softwareType:
              selectedField.id === sendInvoiceSoftware ? 'sendInvoiceSoftware' : 'otherSoftware',
          }
        : {
            definition: {
              id: selectedField.id,
            },
            value: selectedValue ?? null,
          };

    var endpoint =
      selectedField.id === sendInvoiceSoftware || selectedField.id === otherSoftware
        ? 'softwareupdate'
        : null;

    return onSave(fieldUpdate, endpoint);
  };

  closeModal = () => {
    const { onClose } = this.props;

    this.setState(state => ({
      ...state,
      selectedField: undefined,
      selectedValue: undefined,
      newGroupName: '',
      showConfirmCreate: false,
      templateSelections: {},
    }));

    onClose();
  };

  renderTemplateFields() {
    const { intl, languages } = this.props;
    const { resolvedCompanyId, templateSelections, loadingTemplates } = this.state;

    if (!resolvedCompanyId) {
      return (
        <div className="col-md-12">
          <div className="alert alert-warning">
            {intl.formatMessage(messages.FilterOnCompanyRequired)}
          </div>
        </div>
      );
    }

    return (
      <div className="col-md-12">
        {!loadingTemplates &&
          languages.map(langCode => {
            const templatesForLang = this.getTemplatesForLanguage(langCode);
            const currentValue = templateSelections[langCode] || DONT_UPDATE;
            const langLabel = intl.formatMessage(listItemMsg('language', langCode, langCode));

            return (
              <div key={langCode} className="form-group">
                <label className="control-label col-sm-4">{langLabel}</label>
                <div className="col-sm-8">
                  {templatesForLang.length > 0 ? (
                    <select
                      className="form-control"
                      value={currentValue}
                      onChange={e => this.templateChanged(langCode, e.target.value)}
                    >
                      <option value={DONT_UPDATE}>{intl.formatMessage(messages.DontUpdate)}</option>
                      <option value={USE_GENERAL_SETTING}>
                        {intl.formatMessage(messages.UseCompanySetting)}
                      </option>
                      {templatesForLang.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="form-control-static text-danger">
                      {intl.formatMessage(messages.NoTemplateForLanguage)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  render() {
    const { show, intl } = this.props;
    const {
      fields,
      selectedField,
      selectedValue,
      legalForms,
      action,
      selectedItems,
      relationGroups,
      newGroupName,
      showConfirmCreate,
    } = this.state;

    if (!show) return null;

    const isTemplate = selectedField && isTemplateField(selectedField.id);

    return (
      <>
        <OkCancelModal
          title={intl.formatMessage(messages.UpdateField)}
          show={show}
          onClose={this.closeModal}
          disabled={this.isFormInvalid()}
          onSave={() => this.onSave()}
        >
          <div className="form-horizontal">
            <div className="form-group">
              <div className={isTemplate ? 'col-md-12' : 'col-md-6'}>
                {fields && (
                  <select
                    onChange={this.fieldChanged}
                    className="form-control"
                    value={(selectedField && selectedField.id) || ''}
                  >
                    {!selectedField && (
                      <option value="">{intl.formatMessage(messages.SelectAField)}</option>
                    )}
                    {fields.map(field => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {selectedField && !isTemplate && (
                <div className="col-md-6">
                  {(selectedField.id === '68988c03-3b65-4b90-a53a-f4114d9c46bc' ||
                    selectedField.id === '5b5c42ce-0232-43c3-816a-154840dc2b2c' ||
                    selectedField.id === 'bef9f50d-9002-40a0-816f-13c2b507f9f4' ||
                    selectedField.id === '0543ff8b-36bb-4638-b7f6-641484b3ca4b') && (
                    <UserSearch
                      onChange={user => this.selectedValueChanged(user ? user.id : null)}
                      placeholder={intl.formatMessage(messages.SelectAValue)}
                    />
                  )}
                  {selectedField.id === '0443d6e0-343d-437f-907c-84235907a5f8' && (
                    <select
                      onChange={e => {
                        const value = legalForms.find(x => x.id === parseInt(e.target.value));
                        if (!value) return;
                        this.selectedValueChanged(value.id);
                      }}
                      className="form-control"
                      value={selectedValue || ''}
                    >
                      {!selectedValue && selectedValue !== 0 && (
                        <option value="">{intl.formatMessage(messages.SelectAValue)}</option>
                      )}
                      {legalForms &&
                        legalForms.map(legalForm => (
                          <option key={legalForm.id} value={legalForm.id}>
                            {legalForm.label}
                          </option>
                        ))}
                    </select>
                  )}

                  {selectedField.id === 'e31bb7ad-afb4-4a37-bd1e-46d4a9a71cf0' && (
                    <CompanySelector
                      onChange={this.selectedValueChanged}
                      value={selectedValue}
                      required
                    />
                  )}
                  {selectedField.id === '3117d514-64bc-4c6e-84f7-4a39fbdc51e2' && (
                    <DayMonthPicker
                      value={undefined}
                      onChange={date => {
                        if (date) {
                          const dateFormatted = `2000-${date.month + 1}-${date.day}`;
                          this.selectedValueChanged(dateFormatted); // backend expects value: "2000-12-31"
                        }
                      }}
                      id="endFiscalYearDate"
                      required
                      outputType="object"
                      block
                      small
                    />
                  )}

                  {selectedField.id === 'ebf5a67c-6ac2-4616-84ea-2b475f279575' && (
                    <EnumSelect enumType="vatperiodicity" onChange={this.selectedValueChanged} />
                  )}
                  {selectedField.id === 'd4f04a11-cdf7-4d85-900f-c1d15df6057f' && (
                    <EnumSelect
                      enumType="FinancialStatementScheme"
                      onChange={this.selectedValueChanged}
                    />
                  )}
                  {selectedField.id === 'b33726aa-3dbb-4e6a-afce-219670843eb5' && (
                    <EnumSelect
                      enumType="SocialSecretariat"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                    />
                  )}
                  {selectedField.id === '99c41dc8-a3fb-4843-bbbf-1c558c46f90f' && (
                    <EnumSelect
                      enumType="SocialSecurityFund"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                    />
                  )}
                  {selectedField.id === 'b094d68c-1c90-422a-a64a-2913b07e02f1' && (
                    <EnumSelect
                      enumType="paymentMethod"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                    />
                  )}
                  {selectedField.id === '56495285-77d0-46bf-951a-4a66e14e1298' && (
                    <EnumSelect
                      enumType="AccountingSoftware"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                    />
                  )}
                  {selectedField.id === '4326316b-07eb-4ef6-889e-6382b38a23a3' && (
                    <EnumSelect
                      enumType="ReceiveInvoiceSoftware"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                    />
                  )}
                  {selectedField.id === '8a884985-efa6-4faf-8740-6461caf69aed' && (
                    <LanguageSelect onChange={this.selectedValueChanged} />
                  )}
                  {selectedField.id === '246f74be-a60d-46b3-82d7-294dfa227762' && (
                    <EnumSelect
                      enumType="ShareRegister"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                      allowNull
                    />
                  )}
                  {selectedField.id === '40ae0a55-fd7d-4577-830f-22589642e875' && (
                    <EnumSelect
                      enumType="sendInvoiceStrategy"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                      allowNull
                      placeholder={intl.formatMessage(messages.GeneralSetting)}
                    />
                  )}

                  {selectedField.id === '5a9630e2-12c3-4d80-9745-2330bd45ef1a' && (
                    <EnumSelect
                      enumType="peppolReadyStatus"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                      allowNull={false}
                    />
                  )}
                  {selectedField.id === 'b5813c30-a8f9-4b66-a0d5-236f400c5088' && (
                    <EnumSelect
                      enumType="invoiceEmailAttachmentOptions"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                      allowNull
                      placeholder={intl.formatMessage(messages.GeneralSetting)}
                    />
                  )}
                  {(selectedField.id === sendInvoiceSoftware ||
                    selectedField.id === otherSoftware) && (
                    <select onChange={this.actionChanged} className="form-control" value={action}>
                      <option value="add">{intl.formatMessage(messages.Add)}</option>
                      <option value="remove">{intl.formatMessage(messages.Remove)}</option>
                      <option value="replace">{intl.formatMessage(messages.Replace)}</option>
                    </select>
                  )}

                  {selectedField.id === relationGroupField && (
                    <>
                      <select
                        onChange={e => {
                          const val = e.target.value;
                          this.setState({
                            selectedValue: val === '' ? null : val,
                            newGroupName: '',
                          });
                        }}
                        className="form-control"
                        value={
                          newGroupName
                            ? ''
                            : selectedValue === undefined
                            ? '__unset__'
                            : selectedValue ?? ''
                        }
                      >
                        {selectedValue === undefined && !newGroupName && (
                          <option value="__unset__" disabled>
                            {intl.formatMessage(messages.SelectAValue)}
                          </option>
                        )}
                        <option value="">{intl.formatMessage(messages.NoRelationGroup)}</option>
                        {relationGroups.map(g => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="form-control margin5T"
                        placeholder={intl.formatMessage(messages.CreateNewRelationGroup)}
                        value={newGroupName}
                        onChange={e =>
                          this.setState({ newGroupName: e.target.value, selectedValue: undefined })
                        }
                      />
                    </>
                  )}

                  {selectedField.id === invoiceDetailTypeField && (
                    <EnumSelect
                      enumType="invoiceDetailType"
                      onChange={this.selectedValueChanged}
                      orderByLabel
                      allowNull
                      placeholder={intl.formatMessage(messages.UseCompanySetting)}
                    />
                  )}
                </div>
              )}
            </div>
            {isTemplate && (
              <div className="form-group">
                <div className="container-fluid">{this.renderTemplateFields()}</div>
              </div>
            )}
            <div className="form-group">
              <div className="col-md-6 col-md-offset-6">
                {selectedField?.id === sendInvoiceSoftware && (
                  <EnumMultiSelect
                    type="sendInvoiceSoftware"
                    selection={selectedItems}
                    orderByLabel
                    onChange={value => this.setState({ selectedItems: value })}
                    placeholder={intl.formatMessage(messages.Software)}
                  />
                )}

                {selectedField?.id === otherSoftware && (
                  <EnumMultiSelect
                    type="otherSoftware"
                    selection={selectedItems}
                    orderByLabel
                    onChange={value => this.setState({ selectedItems: value })}
                    placeholder={intl.formatMessage(messages.Software)}
                  />
                )}
              </div>
            </div>
          </div>
        </OkCancelModal>
        {showConfirmCreate && (
          <OkCancelModal
            title={intl.formatMessage(tagSettingsMessages.AddRelationGroup)}
            show
            onSave={this.handleConfirmCreateGroup}
            onClose={() => this.setState({ showConfirmCreate: false })}
          >
            <p>
              {intl.formatMessage(messages.ConfirmCreateRelationGroup, { value: newGroupName })}
            </p>
          </OkCancelModal>
        )}
      </>
    );
  }
}
